import useForms from "hooks/useForms";
import { RSVP_APIs } from "libs/apis/rsvp.api";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { siteRoutes } from "utils/helpers/enums/routes";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { ReviewFilterDTO } from "utils/helpers/models/attendee/review-filter-dto";
import { PostJoineeDTO } from "utils/helpers/models/host-dtos/postReviewJoinee.dto";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import useStoreHook from "hooks/useStore";
import { ChangePasswordDTO } from "utils/helpers/models/shared/change-password.dto";

const useRsvp = () => {
  const navigate = useNavigate();
  const { patchFormValues } = useForms();
  const { updateProfileCompletionStatus } = useStoreHook();

  const getRsvpEvent = async (
    setData: Function,
    queryParams: any = {},
    previousData: EventDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await RSVP_APIs.getRsvpEventHome(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new EventDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const getEventById = async (
    id: number,
    setFormData: Function,
    setValue?: Function
  ) => {
    const response = await RSVP_APIs.getEventById(id);
    const { status = false, data = null } = response || {};
    if (status) {
      const { event, host, rsvp = {} } = data;
      const model = new EventDTO({ ...event, host, rsvp });
      if (setValue) {
        patchFormValues(model, setFormData, setValue);
      } else {
        setFormData({ ...model });
      }
    }
  };

  const getMyBadges = async (
    setData: Function,
    queryParams: any,
    setTotalElements?: Function
  ) => {
    const response = await RSVP_APIs.getMyBadges(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { badges, totalElements = 0 } = data;
      setData(badges);
      setTotalElements?.(totalElements);
    }
  };

  const toggleNotification = async () => {
    const response = await RSVP_APIs.toggleNotification();
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const uploadFile = async (file: File): Promise<PictureDTO | null> => {
    const body = new FormData();
    body.append("file", file);
    const response = await RSVP_APIs.uploadFile(body);
    const { status = false, data } = response || {};
    if (status) {
      return new PictureDTO(data);
    } else {
      return null;
    }
  };

  const updateRsvp = async (minorId: number, body: Partial<MinorDTO>) => {
    const response = await RSVP_APIs.updateRsvp(minorId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      
      // Update profileCompleted to true in localStorage
      updateProfileCompletionStatus('rsvp');
      
      navigate(siteRoutes.minorHome);
    }
  };

  const getRsvpProfile = async (setMinorProfileData: Function) => {
    const response = await RSVP_APIs.getRsvpProfile();
    const { status = false, data } = response || {};
    if (status) {
      setMinorProfileData({
        ...data.user,
        ...data.profiles.child,
      });
    }
  };

  const getEventsReviews = async (
    setData: Function,
    queryParams: any = {},
    previousData: ReviewFilterDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await RSVP_APIs.getEventsReviews(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new ReviewFilterDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const getCupGradituteById = async (id: number, setData: Function) => {
    const response = await RSVP_APIs.getCupGradituteHostById(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getEventAttendance = async (eventId: number, setData: Function) => {
    const response = await RSVP_APIs.getEventAttendance(eventId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const postReviewJoinee = async (body: PostJoineeDTO, eventId: number) => {
    const response = await RSVP_APIs.postReviewJoinee(eventId, body);
    const { status = false, message = "Success" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const contactUsSupport = async (body: any) => {
    const response = await RSVP_APIs.contactUsSupport(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const changePassword = async (body: any) => {
    const response = await RSVP_APIs.changePassword(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  return {
    getRsvpEvent,
    getEventById,
    getMyBadges,
    toggleNotification,
    uploadFile,
    updateRsvp,
    getRsvpProfile,
    getEventsReviews,
    getCupGradituteById,
    getEventAttendance,
    postReviewJoinee,
    contactUsSupport,
    changePassword,
  };
};

export default useRsvp;
